import { useEffect, useState } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, Plus, Copy, Check, Mail, Eye, EyeOff, Send, Megaphone, FileText, Shield, Bot, Sparkles } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const BROADCAST_TEMPLATES = [
  {
    id: 'welcome',
    name: 'Welcome New Franchise',
    subject: 'Welcome to Urbanvision Logistics - Getting Started',
    body: `Hi {{name}},

Welcome to the Urbanvision Logistics family! We're thrilled to have {{company}} on board.

Here's how to get started:
1. Log in with your Master Code at our portal
2. Add your agents from User Management
3. Start adding carriers and booking loads
4. Set up your email in Dashboard > Email Settings

If you need any help, don't hesitate to reach out. We're here to support your success!

Best regards,
Urbanvision Logistics Team`
  },
  {
    id: 'feature',
    name: 'New Feature Release',
    subject: 'New Features Available - Urbanvision Logistics',
    body: `Hi {{name}},

We're excited to announce new features now available for {{company}}:

- Enhanced Compliance Tracking (insurance, medical cards, license alerts)
- Agent Performance Dashboard (see who's booking the most loads)
- Email Integration (send emails directly to your agents)
- XLSX Data Export (download your carriers, loads, and driver data)

Log in to your dashboard to explore these features. As always, your feedback helps us build a better platform.

Best regards,
Urbanvision Logistics Team`
  },
  {
    id: 'policy',
    name: 'Policy Update',
    subject: 'Important Policy Update - Urbanvision Logistics',
    body: `Hi {{name}},

We're writing to inform {{company}} about an important policy update effective immediately:

[Describe your policy update here]

Please review these changes and ensure your team is aware. If you have any questions or concerns, please reach out to us directly.

Thank you for your continued partnership.

Best regards,
Urbanvision Logistics Team`
  },
  {
    id: 'payment',
    name: 'Payment Reminder',
    subject: 'Payment Reminder - Urbanvision Logistics',
    body: `Hi {{name}},

This is a friendly reminder that your subscription payment for {{company}} is coming due.

Please ensure your payment method is up to date to avoid any service interruption. You can manage your billing in the platform.

If you've already made the payment, please disregard this message. If you have any billing questions, we're happy to help.

Best regards,
Urbanvision Logistics Team`
  },
  {
    id: 'maintenance',
    name: 'Scheduled Maintenance',
    subject: 'Scheduled Maintenance Notice - Urbanvision Logistics',
    body: `Hi {{name}},

We wanted to give {{company}} advance notice of scheduled platform maintenance:

Date: [Date]
Time: [Time] - [Time] (EST)
Expected Duration: [Duration]

During this window, the platform may be briefly unavailable. We're performing upgrades to improve performance and reliability.

We apologize for any inconvenience and appreciate your patience.

Best regards,
Urbanvision Logistics Team`
  }
];

export default function SuperAdmin({ user }) {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [emailForm, setEmailForm] = useState({ smtp_email: '', smtp_app_password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ subject: '', body: '' });
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [formData, setFormData] = useState({
    company_name: '',
    admin_name: '',
    admin_email: '',
    plan: 'basic'
  });

  // Prevent organization brokers from accessing this page
  if (user.role !== 'super_admin') {
    return (
      <div className="text-center py-12" data-testid="super-admin-page">
        <Shield className="w-16 h-16 mx-auto text-slate-400 mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Super Admin Access Required</h2>
        <p className="text-slate-600">Only super administrators can sell licenses and manage organizations.</p>
      </div>
    );
  }

  useEffect(() => {
    loadOrganizations();
    loadPricing();
  }, []);

  const loadPricing = () => {
    api.get('/pricing/plans')
      .then(res => setPricing(res.data))
      .catch(() => toast.error('Failed to load pricing'));
  };

  const loadOrganizations = () => {
    api.get('/admin/organizations')
      .then(res => setOrganizations(res.data))
      .catch(() => toast.error('Failed to load organizations'))
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    api.post('/admin/organizations', formData)
      .then((res) => {
        toast.success(`Organization created! Master Code: ${res.data.master_code}`);
        setShowForm(false);
        loadOrganizations();
        resetForm();
      })
      .catch(() => toast.error('Failed to create organization'));
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      admin_name: '',
      admin_email: '',
      plan: 'basic'
    });
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success('Master code copied to clipboard!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const suspendOrganization = (orgId) => {
    if (!window.confirm('Suspend this organization? They will lose access.')) return;
    
    api.put(`/admin/organizations/${orgId}`, { status: 'suspended' })
      .then(() => {
        toast.success('Organization suspended');
        loadOrganizations();
      })
      .catch(() => toast.error('Failed to suspend organization'));
  };

  const activateOrganization = (orgId) => {
    api.put(`/admin/organizations/${orgId}`, { status: 'active' })
      .then(() => {
        toast.success('Organization activated');
        loadOrganizations();
      })
      .catch(() => toast.error('Failed to activate organization'));
  };

  const togglePhase3 = (org) => {
    const willEnable = !org.phase3_enabled;
    const msg = willEnable
      ? `Activate Phase 3 AI for "${org.company_name}"? Franchise will be billed +$150 setup + $75/mo.`
      : `Deactivate Phase 3 AI for "${org.company_name}"? All autonomous users will be dropped to Semi-Auto.`;
    if (!window.confirm(msg)) return;
    api.put(`/admin/organizations/${org.id}/phase3`, { enabled: willEnable })
      .then(() => {
        toast.success(`Phase 3 ${willEnable ? 'activated' : 'deactivated'} for ${org.company_name}`);
        loadOrganizations();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };

  const setOrgSeatCap = (org) => {
    const current = org.phase3_seat_cap != null ? String(org.phase3_seat_cap) : '';
    const raw = window.prompt(
      `Phase 3 seat cap for "${org.company_name}"\n\nEnter a number to cap how many Managers can hold autonomous AI.\nLeave blank (or 0) for unlimited.`,
      current
    );
    if (raw === null) return;
    const trimmed = raw.trim();
    const payload = { seat_cap: trimmed === '' ? null : Number(trimmed) };
    api.put(`/admin/organizations/${org.id}/phase3-seat-cap`, payload)
      .then((res) => {
        toast.success(`Seat cap set to ${res.data.phase3_seat_cap ?? 'unlimited'}`);
        loadOrganizations();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed'));
  };


  const deleteOrganization = (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to DELETE the entire franchise "${orgName}"? This will permanently delete ALL data including carriers, shippers, loads, and users. This action CANNOT be undone. Only master admin can delete.`)) {
      return;
    }

    // Double confirmation for such a destructive action
    if (!window.confirm(`FINAL WARNING: You are about to permanently delete "${orgName}" and ALL associated data. Type YES in your mind and click OK to proceed.`)) {
      return;
    }

    api.delete(`/admin/organizations/${orgId}`)
      .then(() => {
        toast.success('Organization deleted successfully');
        loadOrganizations();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to delete organization'));
  };

  const assignAsManager = (orgId, orgName) => {
    if (!window.confirm(`Assign yourself as Broker Manager for "${orgName}"? You'll be able to view their agents and brokers.`)) {
      return;
    }

    api.post(`/users/${user.id}/assign-organization`, { organization_id: orgId })
      .then(() => {
        toast.success('You are now a manager for this franchise!');
        loadOrganizations();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to assign'));
  };

  const unassignAsManager = (orgId, orgName) => {
    if (!window.confirm(`Remove yourself as Broker Manager for "${orgName}"?`)) {
      return;
    }

    api.delete(`/users/${user.id}/unassign-organization/${orgId}`)
      .then(() => {
        toast.success('Access removed');
        loadOrganizations();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to remove access'));
  };

  const openEmailConfig = (org) => {
    setEmailForm({ smtp_email: org.smtp_email || '', smtp_app_password: '' });
    setEmailModal(org);
    setShowPassword(false);
  };

  const saveEmailSettings = (orgId) => {
    const payload = {};
    if (emailForm.smtp_email) payload.smtp_email = emailForm.smtp_email;
    if (emailForm.smtp_app_password) payload.smtp_app_password = emailForm.smtp_app_password;
    if (!payload.smtp_email && !payload.smtp_app_password) {
      toast.error('Enter at least an email address');
      return;
    }
    api.put(`/admin/organizations/${orgId}/email-settings`, payload)
      .then(() => {
        toast.success('Email settings saved');
        setEmailModal(null);
        loadOrganizations();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to save'));
  };

  const sendBroadcast = () => {
    if (!broadcastForm.subject || !broadcastForm.body) {
      toast.error('Subject and message are required');
      return;
    }
    if (selectedOrgs.length === 0) {
      toast.error('Select at least one franchise');
      return;
    }
    setBroadcasting(true);
    setBroadcastResult(null);
    const activeOrgs = organizations.filter(o => o.status === 'active');
    const allSelected = selectedOrgs.length === activeOrgs.length;
    api.post('/admin/broadcast-email', {
      subject: broadcastForm.subject,
      body: broadcastForm.body,
      ...(allSelected ? {} : { org_ids: selectedOrgs })
    })
      .then(res => {
        setBroadcastResult(res.data);
        toast.success(res.data.message);
        if (res.data.sent > 0 && res.data.failed === 0) {
          setTimeout(() => {
            setShowBroadcast(false);
            setBroadcastForm({ subject: '', body: '' });
            setBroadcastResult(null);
            setSelectedOrgs([]);
          }, 3000);
        }
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Broadcast failed'))
      .finally(() => setBroadcasting(false));
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Super Admin Dashboard</h1>
          <p className="text-slate-600 mt-1">Manage organizations and sell software licenses</p>
        </div>
        <div className="flex gap-3">
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => { 
              setShowBroadcast(true); 
              setBroadcastResult(null);
              setSelectedOrgs(organizations.filter(o => o.status === 'active').map(o => o.id));
            }}
            data-testid="broadcast-btn"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Broadcast to All
          </Button>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button data-testid="add-org-btn" className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Sell New License
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Organization</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Company Name *</Label>
                <Input
                  required
                  placeholder="ABC Freight Brokers Inc"
                  value={formData.company_name}
                  onChange={e => setFormData({...formData, company_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Admin Name *</Label>
                <Input
                  required
                  placeholder="John Smith"
                  value={formData.admin_name}
                  onChange={e => setFormData({...formData, admin_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Admin Email *</Label>
                <Input
                  required
                  type="email"
                  placeholder="john@abcfreight.com"
                  value={formData.admin_email}
                  onChange={e => setFormData({...formData, admin_email: e.target.value})}
                />
              </div>
              <div>
                <Label>Plan *</Label>
                {pricing && pricing.promotional_available && (
                  <p className="text-xs text-green-600 font-medium mb-2">🎉 {pricing.promotional_message}</p>
                )}
                <Select value={formData.plan} onValueChange={v => setFormData({...formData, plan: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">
                      Basic - ${pricing?.plans?.basic?.current_price || 99}/mo 
                      {pricing?.promotional_available && <span className="text-green-600"> (Save $49!)</span>}
                    </SelectItem>
                    <SelectItem value="pro">
                      Pro - ${pricing?.plans?.pro?.current_price || 199}/mo
                      {pricing?.promotional_available && <span className="text-green-600"> (Save $49!)</span>}
                    </SelectItem>
                    <SelectItem value="extreme">
                      Extreme - ${pricing?.plans?.extreme?.current_price || 499}/mo
                      {pricing?.promotional_available && <span className="text-green-600"> (Save $100!)</span>}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {pricing && (
                  <p className="text-xs text-slate-500 mt-2">
                    {pricing.promotional_available 
                      ? "Promotional price locked for 1 year from purchase" 
                      : "Regular pricing applies"}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full">Create Organization & Generate Master Code</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Broadcast Email Modal */}
      <Dialog open={showBroadcast} onOpenChange={(open) => { setShowBroadcast(open); if (!open) { setBroadcastResult(null); setSelectedOrgs([]); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-blue-600" />
              {selectedOrgs.length === 1 ? `Email ${organizations.find(o => o.id === selectedOrgs[0])?.company_name || 'Franchise'}` : 'Broadcast to Franchises'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Send from <strong>urbanvisionmag3908@gmail.com</strong> to {selectedOrgs.length} of {organizations.filter(o => o.status === 'active').length} active franchise(s).
            </p>

            {/* Franchise Selection */}
            <div data-testid="franchise-selector">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Recipients</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setSelectedOrgs(organizations.filter(o => o.status === 'active').map(o => o.id))}
                    data-testid="select-all-orgs"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="text-xs text-slate-500 hover:underline"
                    onClick={() => setSelectedOrgs([])}
                    data-testid="deselect-all-orgs"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="max-h-36 overflow-y-auto border rounded-lg divide-y">
                {organizations.filter(o => o.status === 'active').map(org => (
                  <label
                    key={org.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors ${selectedOrgs.includes(org.id) ? 'bg-blue-50' : ''}`}
                    data-testid={`org-checkbox-${org.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrgs.includes(org.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOrgs([...selectedOrgs, org.id]);
                        } else {
                          setSelectedOrgs(selectedOrgs.filter(id => id !== org.id));
                        }
                      }}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{org.company_name}</p>
                      <p className="text-xs text-slate-500 truncate">{org.admin_email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Template Selector */}
            <div>
              <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Quick Templates</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {BROADCAST_TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setBroadcastForm({ subject: t.subject, body: t.body })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                      broadcastForm.subject === t.subject 
                        ? 'bg-blue-50 border-blue-300 text-blue-700' 
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`template-${t.id}`}
                  >
                    <FileText className="w-3 h-3 inline mr-1" />
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
              <p className="font-medium text-slate-700 mb-1">Personalization tags:</p>
              <p><code className="bg-white px-1 rounded">{'{{name}}'}</code> — Franchise admin name</p>
              <p><code className="bg-white px-1 rounded">{'{{company}}'}</code> — Company name</p>
            </div>
            <div>
              <Label>Subject *</Label>
              <Input
                placeholder="Important Update from Urbanvision Logistics"
                value={broadcastForm.subject}
                onChange={e => setBroadcastForm({ ...broadcastForm, subject: e.target.value })}
                data-testid="broadcast-subject"
              />
            </div>
            <div>
              <Label>Message (HTML supported) *</Label>
              <Textarea
                rows={6}
                placeholder={"Hi {{name}},\n\nWe have an exciting update for {{company}}...\n\nBest regards,\nUrbanvision Logistics"}
                value={broadcastForm.body}
                onChange={e => setBroadcastForm({ ...broadcastForm, body: e.target.value })}
                data-testid="broadcast-body"
              />
            </div>
            {broadcastResult && (
              <div className={`rounded-lg p-3 ${broadcastResult.failed > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`} data-testid="broadcast-result">
                <p className={`text-sm font-medium ${broadcastResult.failed > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                  {broadcastResult.message}
                </p>
                {broadcastResult.sent_list?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {broadcastResult.sent_list.map((s, i) => (
                      <p key={i} className="text-xs text-green-600">Sent to {s.company} ({s.email})</p>
                    ))}
                  </div>
                )}
                {broadcastResult.failed_list?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {broadcastResult.failed_list.map((f, i) => (
                      <p key={i} className="text-xs text-red-600">Failed: {f.company} ({f.email})</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={sendBroadcast}
                disabled={broadcasting}
                data-testid="send-broadcast-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {broadcasting ? 'Sending...' : `Send to ${selectedOrgs.length} Franchise(s)`}
              </Button>
              <Button variant="outline" onClick={() => setShowBroadcast(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 gap-4">
        {organizations.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto text-slate-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Organizations Yet</h3>
              <p className="text-slate-600">Create your first organization to start selling licenses</p>
            </CardContent>
          </Card>
        ) : (
          organizations.map(org => (
            <Card key={org.id} className="card-hover" data-testid={`org-card-${org.id}`}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-xl">{org.company_name}</CardTitle>
                    <p className="text-sm text-slate-500 mt-1">Admin: {org.admin_name}</p>
                    <p className="text-sm text-slate-500">{org.admin_email}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    org.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {org.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-slate-50 p-4 rounded-lg border-2 border-dashed border-slate-300">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-600 font-medium mb-1">Master Code</p>
                      <p className="text-2xl font-bold font-mono text-purple-600">{org.master_code}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(org.master_code, org.id)}
                      data-testid={`copy-code-${org.id}`}
                    >
                      {copiedCode === org.id ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Share this code with {org.admin_name} to login as broker
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-bold text-slate-900 capitalize">{org.plan} Plan</p>
                      <p className="text-lg font-bold text-blue-600">${org.monthly_price || 0}/month</p>
                      {org.pricing_tier === 'promotional' && (
                        <p className="text-xs text-green-600 font-medium mt-1">
                          🎉 Promotional (Reg: ${org.regular_price})
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      org.pricing_tier === 'promotional' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-100 text-slate-700'
                    }`}>
                      {org.pricing_tier === 'promotional' ? 'Promo' : 'Regular'}
                    </span>
                  </div>
                  {org.promotional_end_date && (
                    <p className="text-xs text-slate-600 mt-2">
                      Promo ends: {new Date(org.promotional_end_date).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  <div>
                    <p className="text-xs text-slate-500">Created: {new Date(org.created_at).toLocaleDateString()}</p>
                    <p className="text-xs text-slate-500">Signup #{org.signup_number || 'N/A'}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 hover:text-blue-700"
                      onClick={() => openEmailConfig(org)}
                      data-testid={`email-config-${org.id}`}
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" />
                      {org.smtp_email ? 'Email Set' : 'Set Email'}
                    </Button>
                    {org.status === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-indigo-600 hover:text-indigo-700"
                        onClick={() => {
                          setSelectedOrgs([org.id]);
                          setBroadcastForm({ subject: '', body: '' });
                          setBroadcastResult(null);
                          setShowBroadcast(true);
                        }}
                        data-testid={`email-franchise-${org.id}`}
                      >
                        <Send className="w-3.5 h-3.5 mr-1" />
                        Email
                      </Button>
                    )}
                    {user.assigned_organizations?.includes(org.id) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:text-blue-700"
                        onClick={() => unassignAsManager(org.id, org.company_name)}
                      >
                        ✓ Assigned
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => assignAsManager(org.id, org.company_name)}
                      >
                        Assign as Manager
                      </Button>
                    )}
                    {org.status === 'active' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className={org.phase3_enabled ? 'text-amber-700 border-amber-400 bg-amber-50 hover:bg-amber-100' : 'text-slate-600 hover:text-amber-700'}
                        onClick={() => togglePhase3(org)}
                        data-testid={`toggle-phase3-${org.id}`}
                      >
                        {org.phase3_enabled ? (
                          <><Sparkles className="w-3.5 h-3.5 mr-1" />Phase 3 ON</>
                        ) : (
                          <><Bot className="w-3.5 h-3.5 mr-1" />Enable Phase 3</>
                        )}
                      </Button>
                    ) : null}
                    {org.phase3_enabled && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOrgSeatCap(org)}
                        data-testid={`seat-cap-${org.id}`}
                        title="Set Phase 3 seat cap"
                      >
                        Seats: {org.phase3_seat_cap != null ? org.phase3_seat_cap : '∞'}
                      </Button>
                    )}
                    {org.status === 'active' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-orange-600 hover:text-orange-700"
                        onClick={() => suspendOrganization(org.id)}
                      >
                        Suspend
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 hover:text-green-700"
                        onClick={() => activateOrganization(org.id)}
                      >
                        Activate
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => deleteOrganization(org.id, org.company_name)}
                      data-testid={`delete-org-${org.id}`}
                    >
                      Delete Franchise
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Card className="bg-purple-50 border-purple-200">
        <CardHeader>
          <CardTitle className="text-lg">How to Sell Software Licenses</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p><strong>Step 1:</strong> Click "Sell New License" and enter customer details</p>
          <p><strong>Step 2:</strong> System generates unique 6-digit master code automatically</p>
          <p><strong>Step 3:</strong> Share the master code with your customer</p>
          <p><strong>Step 4:</strong> Customer visits: <code className="bg-white px-2 py-1 rounded">https://dispatch-portal-9.preview.emergentagent.com</code></p>
          <p><strong>Step 5:</strong> They enter their master code to become the broker for their company</p>
          <p><strong>Step 6:</strong> They can add their own agents using the platform</p>
          <p className="text-purple-700 font-medium pt-2">🎯 Each organization has complete data isolation - no data sharing between customers!</p>
        </CardContent>
      </Card>

      {/* Email Configuration Modal */}
      <Dialog open={!!emailModal} onOpenChange={() => setEmailModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Configure Franchise Email
            </DialogTitle>
          </DialogHeader>
          {emailModal && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Set up the email for <strong>{emailModal.company_name}</strong>. They'll use this to email their agents directly.
              </p>
              <div>
                <Label>Gmail Address</Label>
                <Input
                  type="email"
                  placeholder="franchise@gmail.com"
                  value={emailForm.smtp_email}
                  onChange={e => setEmailForm({ ...emailForm, smtp_email: e.target.value })}
                  data-testid="email-config-address"
                />
              </div>
              <div>
                <Label>Gmail App Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="xxxx xxxx xxxx xxxx"
                    value={emailForm.smtp_app_password}
                    onChange={e => setEmailForm({ ...emailForm, smtp_app_password: e.target.value })}
                    data-testid="email-config-password"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Franchise owner generates this at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-blue-600 underline">myaccount.google.com/apppasswords</a>
                </p>
              </div>
              {emailModal.smtp_email && (
                <div className="bg-green-50 border border-green-200 rounded p-2">
                  <p className="text-xs text-green-700">Current email: <strong>{emailModal.smtp_email}</strong></p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button className="flex-1" onClick={() => saveEmailSettings(emailModal.id)} data-testid="save-email-config-btn">
                  Save Email Settings
                </Button>
                <Button variant="outline" onClick={() => setEmailModal(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
